import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ANNOUNCE_LIMITS, ANNOUNCE_ROLES, type AnnouncementRow, type Audience, type InboxItem, type MessageStatus } from '@mahallu/shared';
import { ApiError, announce, loadErrorKey, useAnnouncements, useHouse, useHouses, useInbox, useMe, useNotifications, useWards } from '../api';
import { CheckGroup, FormFooter, FormSection, Modal, SelectField, TextAreaField, TextField } from '../components/Form';
import { Icon } from '../components/Icon';
import { Badge, PageHeader } from '../components/Parts';
import { dateTime } from '../format';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

/** Find a house by name, number or head; used by "send to one house" and the janaza alert. */
function HousePicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setTerm(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);
  const houses = useHouses({ q: term || undefined, page: 1, size: 10 });
  return (
    <>
      <TextField label={t('collect.find')} type="search" value={q} onChange={setQ} maxLength={100} />
      <SelectField label={t('col.house')} value={value} onChange={onChange} required empty={t('form.choose')} options={(houses.data?.rows ?? []).map((h) => ({ value: h.id, label: `${h.houseNo} · ${h.nameEn} · ${h.headName ?? ''}` }))} />
    </>
  );
}

function AudienceFields(p: { audience: Audience; setAudience: (a: Audience) => void; wards: string[]; setWards: (w: string[]) => void; houseId?: string; setHouseId?: (h: string) => void; allowHouse: boolean }) {
  const { t } = useTranslation();
  const wards = useWards();
  const choices = (['ALL', 'WARDS', 'PRAVASI', 'HOUSE'] as const).filter((a) => p.allowHouse || a !== 'HOUSE');
  return (
    <>
      <SelectField<Audience> label={t('ann.audience')} value={p.audience} onChange={(v) => v && p.setAudience(v)} required options={choices.map((a) => ({ value: a, label: t(`audience.${a}`) }))} />
      {p.audience === 'WARDS' ? <CheckGroup label={t('mm.wards')} value={p.wards} onChange={p.setWards} full options={(wards.data ?? []).map((w) => ({ value: w.code, label: `${w.code} · ${w.nameEn}` }))} /> : null}
      {p.audience === 'HOUSE' && p.setHouseId ? <HousePicker value={p.houseId ?? ''} onChange={p.setHouseId} /> : null}
    </>
  );
}

function NewAnnouncement({ preset, onClose }: { preset: Audience; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [titleEn, setTitleEn] = useState('');
  const [titleMl, setTitleMl] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [bodyMl, setBodyMl] = useState('');
  const [audience, setAudience] = useState<Audience>(preset);
  const [wards, setWards] = useState<string[]>([]);
  const [houseId, setHouseId] = useState('');
  const save = useMutation({
    mutationFn: () =>
      announce.create({ titleEn: titleEn.trim(), titleMl: titleMl.trim() || null, bodyEn: bodyEn.trim(), bodyMl: bodyMl.trim() || null, audience, wardCodes: audience === 'WARDS' ? wards : undefined, houseId: audience === 'HOUSE' ? houseId : null }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['announcements'] });
      onClose();
    },
  });
  return (
    <Modal title={preset === 'HOUSE' ? t('ann.sendTitle') : t('ann.new')} onClose={onClose} wide footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('ann.submit')} error={save.isError ? errorText(save.error) : undefined} />}>
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className="callout small">{t('ann.howItGoes')}</p>
        <FormSection title={t('ann.message')}>
          <TextField label={t('ann.titleEn')} value={titleEn} onChange={setTitleEn} maxLength={ANNOUNCE_LIMITS.title} required />
          <TextField label={t('ann.titleMl')} value={titleMl} onChange={setTitleMl} maxLength={ANNOUNCE_LIMITS.title} lang="ml" />
          <TextAreaField label={t('ann.bodyEn')} value={bodyEn} onChange={setBodyEn} maxLength={ANNOUNCE_LIMITS.body} rows={4} required />
          <TextAreaField label={t('ann.bodyMl')} value={bodyMl} onChange={setBodyMl} maxLength={ANNOUNCE_LIMITS.body} rows={4} lang="ml" />
        </FormSection>
        <FormSection title={t('ann.to')}>
          <AudienceFields audience={audience} setAudience={setAudience} wards={wards} setWards={setWards} houseId={houseId} setHouseId={setHouseId} allowHouse />
        </FormSection>
      </form>
    </Modal>
  );
}

function JanazaForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [houseId, setHouseId] = useState('');
  const house = useHouse(houseId || null);
  const [memberId, setMemberId] = useState('');
  const [prayerAt, setPrayerAt] = useState('');
  const [prayerPlace, setPrayerPlace] = useState('');
  const [burialPlace, setBurialPlace] = useState('');
  const [audience, setAudience] = useState<Audience>('ALL');
  const [wards, setWards] = useState<string[]>([]);
  const deceased = (house.data?.members ?? []).filter((m) => m.status === 'DECEASED');
  const save = useMutation({
    mutationFn: () => announce.janaza({ deceasedMemberId: memberId, prayerAt, prayerPlace: prayerPlace.trim(), burialPlace: burialPlace.trim(), audience: audience as Exclude<Audience, 'HOUSE'>, wardCodes: audience === 'WARDS' ? wards : undefined }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['announcements'] });
      onClose();
    },
  });
  return (
    <Modal title={t('janaza.title')} onClose={onClose} wide footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('janaza.send')} error={save.isError ? errorText(save.error) : undefined} />}>
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className="callout small">{t('janaza.hint')}</p>
        <FormSection title={t('janaza.person')}>
          <HousePicker value={houseId} onChange={(id) => { setHouseId(id); setMemberId(''); }} />
          <SelectField label={t('requests.person')} value={memberId} onChange={setMemberId} required empty={t('form.choose')} hint={t('cert.deceasedHint')} options={deceased.map((m) => ({ value: m.id, label: m.nameEn }))} />
        </FormSection>
        <FormSection title={t('janaza.details')}>
          <TextField label={t('janaza.prayerAt')} type="datetime-local" value={prayerAt} onChange={setPrayerAt} required />
          <TextField label={t('janaza.prayerPlace')} value={prayerPlace} onChange={setPrayerPlace} maxLength={ANNOUNCE_LIMITS.place} required />
          <TextField label={t('cert.burialPlace')} value={burialPlace} onChange={setBurialPlace} maxLength={ANNOUNCE_LIMITS.place} required />
          <AudienceFields audience={audience} setAudience={setAudience} wards={wards} setWards={setWards} allowHouse={false} />
        </FormSection>
      </form>
    </Modal>
  );
}

function RejectForm({ a, onClose }: { a: AnnouncementRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => announce.reject(a.id, reason.trim(), a.version),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['announcements'] });
      onClose();
    },
  });
  return (
    <Modal title={t('requests.reject')} onClose={onClose} footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('requests.reject')} error={save.isError ? errorText(save.error) : undefined} />}>
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormSection title={a.titleEn}>
          <TextAreaField label={t('rcpt.reason')} value={reason} onChange={setReason} maxLength={300} rows={3} required full />
        </FormSection>
      </form>
    </Modal>
  );
}

const REACH_ORDER: MessageStatus[] = ['QUEUED', 'SENDING', 'SENT', 'DELIVERED', 'READ', 'FAILED'];

/** Communication › Announcements and Send Message (pilot slice 10). */
export function Announcements() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const me = useMe();
  const location = useLocation();
  const canWrite = Boolean(me.data?.role && ANNOUNCE_ROLES.includes(me.data.role));
  const list = useAnnouncements();
  const [form, setForm] = useState<'new' | 'house' | 'janaza' | null>(location.pathname.endsWith('/send') ? 'house' : null);
  const [rejecting, setRejecting] = useState<AnnouncementRow | null>(null);
  const approve = useMutation({ mutationFn: (a: AnnouncementRow) => announce.approve(a.id, a.version), onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }) });
  const ml = i18n.language === 'ml';
  return (
    <>
      <PageHeader
        icon="mega"
        title={t('ann.title')}
        desc={t('ann.desc')}
        actions={
          canWrite
            ? [
                { label: t('janaza.title'), icon: 'bell', onClick: () => setForm('janaza') },
                { label: t('ann.sendTitle'), icon: 'mega', onClick: () => setForm('house') },
                { label: t('ann.new'), icon: 'plus', primary: true, onClick: () => setForm('new') },
              ]
            : []
        }
      />
      {approve.isError ? <p className="form-error" role="alert">{errorText(approve.error)}</p> : null}
      {list.isError ? <p className="form-error">{t(loadErrorKey(list.error))}</p> : null}
      <div className="privacy-grid">
        {(list.data ?? []).map((a) => (
          <section key={a.id} className="card">
            <div className="section-h">
              {a.kind === 'JANAZA' ? <Badge tone="neutral">{t('janaza.badge')}</Badge> : null}
              <h2 className={ml && a.titleMl ? 'ml' : undefined}>{ml && a.titleMl ? a.titleMl : a.titleEn}</h2>
              <Badge tone={a.state === 'SENT' ? 'ok' : a.state === 'REJECTED' ? 'neutral' : 'warn'}>{t(`astate.${a.state}`)}</Badge>
              <span className="spacer" />
              {a.canApprove ? (
                <>
                  <button type="button" className="btn sm primary" disabled={approve.isPending} onClick={() => approve.mutate(a)}>
                    <Icon name="check" />
                    {t('ann.approve')}
                  </button>
                  <button type="button" className="btn sm" onClick={() => setRejecting(a)}>
                    {t('requests.reject')}
                  </button>
                </>
              ) : null}
            </div>
            <p className={ml && a.bodyMl ? 'ml' : undefined} style={{ whiteSpace: 'pre-wrap' }}>
              {ml && a.bodyMl ? a.bodyMl : a.bodyEn}
            </p>
            <p className="note">
              {t(`audience.${a.audience}`)}
              {a.wards.length ? ` · ${a.wards.join(', ')}` : ''}
              {a.house ? ` · ${a.house.houseNo} ${a.house.nameEn}` : ''} · {t('ann.by', { name: a.createdBy, when: dateTime(a.createdAt, i18n.language) })}
              {a.approvedBy && a.state === 'SENT' && a.kind === 'NOTICE' ? ` · ${t('ann.approvedBy', { name: a.approvedBy })}` : ''}
            </p>
            {a.state === 'SENT' ? (
              <div className="filters" style={{ marginTop: 6 }}>
                <Badge tone="info">{t('ann.reached', { n: a.reach.recipients })}</Badge>
                {REACH_ORDER.filter((s) => a.reach.byStatus[s]).map((s) => (
                  <Badge key={s} tone={s === 'FAILED' ? 'bad' : s === 'DELIVERED' || s === 'READ' ? 'ok' : 'neutral'}>
                    {t(`mstatus2.${s}`)} {a.reach.byStatus[s]}
                  </Badge>
                ))}
                {a.reach.skippedConsent ? <span className="note">{t('ann.skippedConsent', { n: a.reach.skippedConsent })}</span> : null}
                {a.reach.skippedPhone ? <span className="note">{t('ann.skippedPhone', { n: a.reach.skippedPhone })}</span> : null}
              </div>
            ) : null}
            {a.rejectReason ? <p className="note">{a.rejectReason}</p> : null}
          </section>
        ))}
        {list.data && !list.data.length ? <p className="note">{t('ann.none')}</p> : null}
      </div>
      {form === 'new' || form === 'house' ? <NewAnnouncement preset={form === 'house' ? 'HOUSE' : 'ALL'} onClose={() => setForm(null)} /> : null}
      {form === 'janaza' ? <JanazaForm onClose={() => setForm(null)} /> : null}
      {rejecting ? <RejectForm a={rejecting} onClose={() => setRejecting(null)} /> : null}
    </>
  );
}

/** Notifications (pilot slice 10): what happened that concerns the signed-in person. */
export function Notifications() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const list = useNotifications();
  const read = useMutation({ mutationFn: () => announce.markRead(), onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }) });
  return (
    <>
      <PageHeader icon="bell" title={t('notif.title')} desc={t('notif.desc')} actions={list.data?.unread ? [{ label: t('notif.markAll'), icon: 'check', onClick: () => read.mutate() }] : []} />
      {list.isError ? <p className="form-error">{t(loadErrorKey(list.error))}</p> : null}
      <ul className="history card">
        {(list.data?.rows ?? []).map((n) => (
          <li key={n.id} className={n.read ? 'muted-row' : undefined}>
            {!n.read ? <Badge tone="info">{t('notif.new')}</Badge> : null} {n.link ? <Link to={n.link}>{t(`notif.${n.kind}`, n.params)}</Link> : t(`notif.${n.kind}`, n.params)}
            <div className="note">{dateTime(n.createdAt, i18n.language)}</div>
          </li>
        ))}
        {list.data && !list.data.rows.length ? <li className="note">{t('notif.none')}</li> : null}
      </ul>
    </>
  );
}

/** Approvals (pilot slice 10, GOV-05): what waits for the signed-in person in their current role. */
export function Approvals() {
  const { t, i18n } = useTranslation();
  const inbox = useInbox();
  const items = inbox.data ?? [];
  const label = (i: InboxItem) => t(`inbox.${i.kind}`, { label: i.label });
  return (
    <>
      <PageHeader icon="check" title={t('inbox.title')} desc={t('inbox.desc')} />
      {inbox.isError ? <p className="form-error">{t(loadErrorKey(inbox.error))}</p> : null}
      <ul className="history card">
        {items.map((i, n) => (
          <li key={`${i.kind}-${i.id ?? n}`}>
            {i.overdue ? <Badge tone="bad">{t('requests.overdue')}</Badge> : null} <Link to={i.link}>{label(i)}</Link>
            {i.since ? <div className="note">{dateTime(i.since, i18n.language)}</div> : null}
          </li>
        ))}
        {inbox.data && !items.length ? <li className="note">{t('inbox.none')}</li> : null}
      </ul>
    </>
  );
}
